import { useEffect, useState } from "react";
import { getClient, getClientBookings } from "./clientResolver";
import { STATUS_LABELS } from "./schema";
import { STAFF_THEME, FONT } from "../../theme/staff";

const BRAND = { ...STAFF_THEME, bg: STAFF_THEME.dark, line: STAFF_THEME.raised };

const pillStyle = {
  background: BRAND.panel,
  color: BRAND.cream,
  border: `1px solid ${BRAND.line}`,
  borderRadius: "999px",
  padding: "0.4rem 0.85rem",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "0.85rem",
};

function fmtDate(v) {
  if (!v) return null;
  const d = v?.toDate ? v.toDate() : new Date(v);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

export default function ClientDetail({ id, navigate }) {
  const [client, setClient] = useState(undefined); // undefined = loading, null = not found
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const c = await getClient(id);
        const bks = await getClientBookings(id);
        if (alive) { setClient(c); setBookings(bks); }
      } catch { if (alive) setClient(null); }
    })();
    return () => { alive = false; };
  }, [id]);

  const wrap = (children) => (
    <div style={{ background: BRAND.bg, minHeight: "100vh", color: BRAND.text, fontFamily: FONT.body, padding: "1rem", boxSizing: "border-box" }}>
      <div style={{ maxWidth: "720px", margin: "0 auto" }}>{children}</div>
    </div>
  );

  if (client === undefined) {
    return wrap(<div style={{ color: BRAND.muted, paddingTop: "3rem", textAlign: "center" }}>Loading client…</div>);
  }
  if (client === null) {
    return wrap(
      <>
        <button type="button" onClick={() => navigate("/crm")} style={pillStyle}>← Bookings</button>
        <div style={{ color: BRAND.pink, marginTop: "1rem" }}>Client not found.</div>
      </>
    );
  }

  const revenue = bookings.reduce((s, b) => s + (Number(b.totalRate) || 0), 0);
  const display = client.name || client.company || client.email || "Client";

  return wrap(
    <>
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
        <h1 style={{ margin: 0, fontSize: "1.3rem", color: BRAND.gold, flex: 1 }}>👤 {display}</h1>
        <button type="button" onClick={() => navigate("/crm")} style={pillStyle}>← Bookings</button>
      </div>

      {/* Contact + totals */}
      <div style={{ background: BRAND.panel, border: `1px solid ${BRAND.line}`, borderRadius: "10px", padding: "0.9rem", marginBottom: "1rem" }}>
        {client.company && <div style={{ color: BRAND.cream, fontSize: "0.9rem" }}>{client.company}</div>}
        {client.email && <div style={{ color: BRAND.muted, fontSize: "0.85rem" }}>{client.email}</div>}
        {client.phone && <div style={{ color: BRAND.muted, fontSize: "0.85rem" }}>{client.phone}</div>}
        <div style={{ display: "flex", gap: "1.2rem", marginTop: "0.7rem" }}>
          <div>
            <div style={{ color: BRAND.gold, fontWeight: 800, fontSize: "1.2rem" }}>{bookings.length}</div>
            <div style={{ color: BRAND.muted, fontSize: "0.72rem" }}>event{bookings.length === 1 ? "" : "s"}</div>
          </div>
          <div>
            <div style={{ color: BRAND.gold, fontWeight: 800, fontSize: "1.2rem" }}>${revenue.toLocaleString()}</div>
            <div style={{ color: BRAND.muted, fontSize: "0.72rem" }}>total booked rate</div>
          </div>
        </div>
      </div>

      {/* Events */}
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.85rem", marginBottom: "0.5rem", letterSpacing: "0.04em" }}>
        EVENTS
      </div>
      {bookings.length === 0 ? (
        <div style={{ color: BRAND.muted, fontSize: "0.85rem" }}>No events linked to this client yet.</div>
      ) : (
        bookings.map((b) => {
          const title = b.eventName || b.name || "(untitled)";
          const date = fmtDate(b.confirmedDate) || fmtDate(b.inquiryEventDate) || "No date";
          return (
            <button
              key={b.id}
              type="button"
              onClick={() => navigate(`/crm/bookings/${b.id}`)}
              style={{
                display: "flex", alignItems: "center", gap: "0.6rem", width: "100%", textAlign: "left",
                background: BRAND.panel, border: `1px solid ${BRAND.line}`, borderRadius: "8px",
                padding: "0.6rem 0.7rem", marginBottom: "0.4rem", cursor: "pointer",
              }}
            >
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: "block", color: BRAND.cream, fontWeight: 700, fontSize: "0.9rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
                <span style={{ display: "block", color: BRAND.muted, fontSize: "0.76rem" }}>
                  {date}{b.venueName ? ` · ${b.venueName}` : ""}{b.totalRate ? ` · $${Number(b.totalRate).toLocaleString()}` : ""}
                </span>
              </span>
              <span style={{ color: BRAND.muted, fontSize: "0.74rem", flexShrink: 0 }}>{STATUS_LABELS[b.status] || b.status || ""}</span>
            </button>
          );
        })
      )}
    </>
  );
}
